import { rupeeGross } from "./priceList";
import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";

/**
 * Plans › Propose from a document (plan builder design §9.13 step 3, owner
 * 2026-10-10).
 *
 *   POST /radagent/propose/{ns}        start: the document, base64 -> 202 {id}
 *   GET  /radagent/propose/{ns}/{id}   poll until the job is no longer "running"
 *
 * radagent has the model copy the document's prices into a fixed form, checks
 * every quote against the document, and renders a price-list file marked NOT
 * APPROVED. Nothing is charged from it: the owner approves a proposal by
 * merging it (E1, E2). This screen shows the result and lets the operator
 * download the file.
 */

/** radagent's limit on the document itself (RADAGENT_PROPOSE_MAX_BYTES). */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

export const ACCEPTED = ".pdf,.docx,.txt,.md,.csv";

export type ProposalStatus = "running" | "proposed" | "rejected" | "needs_answers" | "failed";

export interface ProposedPlan {
  product: string;
  speed_mbps: number;
  term_days: number;
  price: number;
  /** Logins the plan includes, when the file states it. */
  devices?: number | null;
  /** Overrides the document's default; false = the figure is net of GST. */
  includes_gst?: boolean | null;
  quote: string;
}

/** Any value-added service the document sells; none is known by name. */
export interface ProposedCharge {
  charge: string;
  /** [] = sold alone; otherwise the plan products it needs. */
  depends_on?: string[];
  priced_by?: "plan term" | "one price" | "own term" | "formula";
  /** A rupee figure, "Free", or a formula string starting with "=". */
  prices: Record<string, number | string>;
  includes_gst?: boolean | null;
  quote: string;
}

/** How one charge is billed, and what it raises on the RADIUS login. Set by a person. */
export interface Binding {
  key: string;
  billing: "RECURRING" | "ONE_TIME";
  category: string;
  sac: string;
  uqc: string;
  accounting: string;
  internet_service: boolean;
  /** e.g. "Simultaneous-Use +1 per unit"; null = changes nothing on the login. */
  effect: string | null;
  /** ONE_TIME only: "address" = once per installation address of the sale (installation, a router sold outright; owner PC-1/PC-2). Absent = once per sale. radagent v0.1.16+. */
  charged_per?: "address" | undefined;
}

/** The bindings as sent: charged_per only where it is "address" on a ONE_TIME charge, so an older radagent is never sent a key it refuses. */
export function bindingsBody(bindings: Record<string, Binding>): Record<string, Binding> {
  return Object.fromEntries(
    Object.entries(bindings).map(([n, b]) => {
      const { charged_per, ...rest } = b;
      return [n, charged_per === "address" && b.billing === "ONE_TIME" ? { ...rest, charged_per } : rest];
    }),
  );
}

export interface ApplyResult {
  status: "running" | "applied" | "failed";
  created?: { component: number; price_rule: number };
  ended?: number;
  repriced?: number;
  unchanged?: number;
  effective_from?: string;
  approved_file?: string;
  left_out?: string[];
  /** applied with retire: what was retired (rules ended, components withdrawn). */
  retired?: string[];
  /** failed: rows an earlier Approve wrote that this proposal no longer has -- what retiring would settle. */
  retirable?: string[];
  /** applied with restore: what came back (components restored, prices re-created). */
  restored?: string[];
  left_alone?: string[];
  skipped_plans?: { file_name: string; plans: string[] }[];
  /** applied (radagent v0.1.15+): every basic plan the list prices, put on sale (owner 2026-10-26). */
  plans_on_sale?: string[];
  /** applied: basic plans the gateway refused to build, with why; a rerun finishes them. */
  plans_not_on_sale?: string[];
  /** failed: rows retired earlier that this proposal carries again -- what restoring would settle. */
  restorable?: string[];
  error?: string;
}

/** A charge billing already holds for this namespace (radagent v0.1.14+). */
export interface ExistingCharge {
  charge: string;
  key: string;
  own_term: boolean;
  one_time: boolean;
}

export interface ProposalJob {
  id: string;
  namespace: string;
  status: ProposalStatus;
  /** radagent lists this namespace as a TEST namespace a proposal may be applied to. */
  can_apply?: boolean;
  /** radagent honours leave_out on apply (v0.1.8+); only then are Include boxes shown. */
  can_leave_out?: boolean;
  apply?: ApplyResult;
  source: { filename: string; sha256: string };
  /** proposed */
  rendered?: string;
  rows?: number;
  needs_binding?: string[];
  /** Charges billing already holds; the AI was told to reuse their names. */
  existing?: ExistingCharge[];
  changes?: { added: string[]; repriced: string[]; dropped: string[] };
  proposal?: {
    plans?: ProposedPlan[];
    addons?: ProposedCharge[];
    one_time?: ProposedCharge[];
    /** Read but not copied: a deposit, or one item the AI was unsure of ("not copied: ..."). */
    excluded?: { charge: string; reason: string; quote: string }[];
    effective_from?: string;
    prices_include_gst?: boolean | null;
  };
  /** rejected */
  quote_failures?: string[];
  /** needs_answers */
  problems?: string[];
  /** A typed prompt: how each price was calculated. */
  working?: { product: string; speed_mbps: number; term_days: number; price: number; working: string }[];
  /** A typed prompt: plans already sold at this speed and days. */
  warnings?: string[];
  /** failed */
  error?: string;
}

/** Who sees the screen: the gateway's agent_propose holders. */
export function canPropose(roles: string[]): boolean {
  return roles.some((r) => ["superadmin", "bssadmin", "operator", "manager"].includes(r.toLowerCase()));
}

/** Refused before upload, with the reason; null when the file may go. */
export function uploadProblem(file: { name: string; size: number }): string | null {
  if (file.size === 0) return "The file is empty.";
  if (file.size > MAX_UPLOAD_BYTES) return `The file is ${(file.size / 1048576).toFixed(1)} MB; the limit is 5 MB.`;
  const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
  if (!ACCEPTED.split(",").includes(ext)) return "Upload a PDF, Word (.docx) or text file. Photos of a rate card are not read yet.";
  return null;
}

/** Bytes to base64 without a data: prefix, in chunks so a large file does not overflow the call stack. */
export function toBase64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(s);
}

const enc = encodeURIComponent;

export async function startProposal(
  identity: CallerIdentity,
  ns: string,
  file: File,
  effectiveFrom?: string,
): Promise<ProposalJob> {
  const content_base64 = toBase64(new Uint8Array(await file.arrayBuffer()));
  const body: Record<string, string> = { namespace: ns, filename: file.name, media_type: file.type, content_base64 };
  if (effectiveFrom) body.effective_from = effectiveFrom;
  const r = await callGateway<ProposalJob>(identity, config().gatewayBase, `/radagent/propose/${enc(ns)}`, { method: "POST", body });
  return r.data;
}

/** New plans from typed words (owner 2026-11-01): same job as a document, kind "prompt". */
export async function startPlanPrompt(identity: CallerIdentity, ns: string, typed: string, effectiveFrom?: string): Promise<ProposalJob> {
  const content_base64 = toBase64(new TextEncoder().encode(typed));
  const body: Record<string, string> = { namespace: ns, filename: "typed-plans.txt", media_type: "text/plain", content_base64, kind: "prompt" };
  if (effectiveFrom) body.effective_from = effectiveFrom;
  const r = await callGateway<ProposalJob>(identity, config().gatewayBase, `/radagent/propose/${enc(ns)}`, { method: "POST", body });
  return r.data;
}

export async function readProposal(identity: CallerIdentity, ns: string, id: string, signal?: AbortSignal): Promise<ProposalJob> {
  const r = await callGateway<ProposalJob>(identity, config().gatewayBase, `/radagent/propose/${enc(ns)}/${enc(id)}`, signal ? { signal } : {});
  return r.data;
}

/** The headline for a finished job. */
export function headline(job: ProposalJob): { severity: "success" | "warning" | "error" | "info"; title: string } {
  switch (job.status) {
    case "running":
      return { severity: "info", title: "Reading the document…" };
    case "proposed":
      return { severity: "success", title: "Proposal ready — not approved, nothing is charged from it" };
    case "rejected":
      return { severity: "error", title: "Not proposed: some figures could not be found in the document" };
    case "needs_answers":
      return { severity: "warning", title: "Not proposed: the document leaves questions open" };
    default:
      return { severity: "error", title: "Could not read the document" };
  }
}

const plural = (n: string) => `${n} day${n === "1" ? "" : "s"}`;

/** The compiler's row keys, readable. Anything else passes through. */
export function describeRow(logical: string): string {
  const m = /^(residential|hotspot|commercial)\/(\d+)M\/(\d+)d$/.exec(logical);
  if (m) return `${m[1]![0]!.toUpperCase()}${m[1]!.slice(1)} ${m[2]} Mbps, ${plural(m[3]!)}`;
  const parts = /^addon\/([a-z0-9_]+)(?:\/(\d+)d)?(?:\/under\/([a-z]+)(?:\/(\d+)M)?(?:\/(\d+)d)?)?$/.exec(logical);
  if (!parts) return logical;
  const [, key, own, product, speed, term] = parts;
  let s = key!.replace(/_/g, " ");
  if (own) s += `, ${plural(own)}`;
  if (product) s += ` under ${product}${speed ? ` ${speed} Mbps` : ""}${term ? ` ${term}-day` : ""} plans`;
  return s;
}

const rupees = (v: number) =>
  `₹${v.toLocaleString("en-IN", { minimumFractionDigits: Number.isInteger(v) ? 0 : 2, maximumFractionDigits: 2 })}`;

/** Whether an item's figure includes GST: its own flag, else the document's. */
export function includesGst(item: { includes_gst?: boolean | null }, documentDefault?: boolean | null): boolean | null {
  if (typeof item.includes_gst === "boolean") return item.includes_gst;
  return typeof documentDefault === "boolean" ? documentDefault : null;
}

/** The final amount of a net figure: GST at 18% added, in whole rupees as billing charges it. */
export function withGst(net: number): number {
  return rupeeGross(net, 18);
}

/**
 * A price cell: rupees, Free, or the formula as written. A figure net of GST
 * shows the final amount too (owner 2026-10-10).
 */
export function chargePrice(v: number | string, gst: boolean | null = true): string {
  if (typeof v === "number") {
    return gst === false ? `${rupees(v)} + GST = ${rupees(withGst(v))}` : rupees(v);
  }
  const s = v.trim();
  if (s.startsWith("=")) return gst === false ? `${s} + GST` : s;
  return s;
}

/** Who a charge is sold to, in words. */
export function soldWith(c: ProposedCharge): string {
  return c.depends_on && c.depends_on.length > 0 ? `with ${c.depends_on.join(", ")} plans` : "sold alone";
}

/** "Extra login, per device" -> "extra_login_per_device". */
export function chargeKey(name: string): string {
  const k = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return /^[a-z]/.test(k) ? k : `charge_${k}`;
}

/**
 * A starting point for a charge's billing that the person reviews -- the
 * common case for an ISP service, never a guess about a named charge.
 */
export function defaultBinding(charge: string, oneTime: boolean): Binding {
  return {
    key: chargeKey(charge),
    billing: oneTime ? "ONE_TIME" : "RECURRING",
    category: oneTime ? "ONEOFF" : "INTERNET",
    sac: "998422",
    uqc: "NOS",
    accounting: oneTime ? "INSTALLATION" : "INTERNET_REVENUE",
    internet_service: true,
    effect: null,
  };
}

/** What radagent would refuse, said before sending. */
export function bindingProblems(b: Binding): string[] {
  const out: string[] = [];
  if (!/^[a-z][a-z0-9_]*$/.test(b.key)) out.push("key: lower case, digits and _ only");
  if (!/^[A-Z][A-Z_]*$/.test(b.category)) out.push("category: UPPER_SNAKE");
  if (!/^[A-Z][A-Z_]*$/.test(b.accounting)) out.push("accounting: UPPER_SNAKE");
  if (!/^[0-9]{4,8}$/.test(b.sac)) out.push("SAC/HSN: 4 to 8 digits");
  if (!/^[A-Z]{3}$/.test(b.uqc)) out.push("UQC: three capital letters");
  if (b.effect && !/^[A-Za-z][A-Za-z0-9-]* \+\d+ per unit$/.test(b.effect)) out.push("RADIUS effect: '<attribute> +<n> per unit'");
  return out;
}

/** A proposal this browser started or opened, so it can be found again without its link. */
export interface RecentProposal {
  id: string;
  namespace: string;
  filename: string;
  /** ms since epoch, when first seen */
  at: number;
}

const RECENT_KEY = "ankhos.proposals.recent";
/** radagent keeps a finished proposal an hour (RADAGENT_PROPOSE_JOB_TTL). */
export const RECENT_TTL_MS = 60 * 60 * 1000;

export function recentProposals(now = Date.now()): RecentProposal[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]") as RecentProposal[];
    return Array.isArray(v) ? v.filter((r) => r && typeof r.id === "string" && now - r.at < RECENT_TTL_MS) : [];
  } catch {
    return [];
  }
}

export function rememberProposal(r: Omit<RecentProposal, "at">, now = Date.now()): RecentProposal[] {
  const list = recentProposals(now);
  const old = list.find((x) => x.id === r.id);
  const next = [{ ...r, at: old?.at ?? now }, ...list.filter((x) => x.id !== r.id)].slice(0, 20);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // private window or blocked storage: the list is a convenience only
  }
  return next;
}

/** Drop a proposal radagent no longer holds (expired, or radagent restarted). */
export function forgetProposal(id: string, now = Date.now()): RecentProposal[] {
  const next = recentProposals(now).filter((r) => r.id !== id);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // private window or blocked storage: the list is a convenience only
  }
  return next;
}

/**
 * "net.example price_rule addon/router_rental/under/residential/90d" ->
 * "price · router rental under residential 90-day plans".
 */
export function describeRetirable(s: string): string {
  const m = /^\S+ (component|price_rule) (.+)$/.exec(s);
  if (!m) return s;
  return `${m[1] === "component" ? "component" : "price"} · ${describeRow(m[2]!)}`;
}

/** A proposed plan as radagent and the compiler name it: "residential/30M/90d". */
export function planId(x: Pick<ProposedPlan, "product" | "speed_mbps" | "term_days">): string {
  return `${x.product}/${x.speed_mbps}M/${x.term_days}d`;
}

/**
 * Approve a proposal for a TEST namespace: radagent writes its rows as the
 * caller. `leaveOut` names the plans (planId) and charges the person did not
 * approve; none of them is written.
 */
export async function applyProposal(
  identity: CallerIdentity,
  ns: string,
  id: string,
  bindings: Record<string, Binding>,
  leaveOut: string[] = [],
  retire = false,
  restore = false,
  rename: Record<string, string> = {},
  termDiscount: TermDiscount = DEFAULT_TERM_DISCOUNT,
): Promise<ProposalJob> {
  const r = await callGateway<ProposalJob>(identity, config().gatewayBase, `/radagent/propose/${enc(ns)}/${enc(id)}/apply`, {
    method: "POST",
    body: {
      namespace: ns,
      bindings: bindingsBody(bindings),
      leave_out: leaveOut,
      ...(retire ? { retire: true } : {}),
      ...(restore ? { restore: true } : {}),
      ...(Object.keys(rename).length ? { rename } : {}),
      term_discount: termDiscount,
    },
  });
  return r.data;
}

const words = (s: string) => new Set(s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean));

/**
 * The charge billing already holds that `name` most likely is: the same name
 * or key, else the one whose words contain the other's (or are contained),
 * sharing the most words. A tie is no guess. 2026-10-11: one reading said
 * "Extra login", the next "Extra login (one more device online at the same
 * time)", and billing treated them as two items.
 */
export function sameItem(name: string, existing: ExistingCharge[]): ExistingCharge | undefined {
  const exact = existing.find((e) => e.charge === name || e.key === chargeKey(name));
  if (exact) return exact;
  const mine = words(name);
  let best: ExistingCharge | undefined;
  let bestShared = 0;
  let tie = false;
  for (const e of existing) {
    const theirs = words(e.charge);
    const shared = [...mine].filter((w) => theirs.has(w)).length;
    if (shared === 0 || shared < Math.min(mine.size, theirs.size)) continue;
    if (shared > bestShared) {
      best = e;
      bestShared = shared;
      tie = false;
    } else if (shared === bestShared) tie = true;
  }
  return tie ? undefined : best;
}

/**
 * Term discount, percent by term (owner 2026-10-26): a residential speed the
 * document prices only for 30 days is sold for 90/180/365 days at the 30-day
 * price × months less this. The compiler applies it; a term the document
 * prices keeps its own price.
 */
export type TermDiscount = Record<"90" | "180" | "365", number>;

export const DEFAULT_TERM_DISCOUNT: TermDiscount = { "90": 5, "180": 10, "365": 15 };

/** What is wrong with a discount: each term 0 to 50, at most 2 decimals. */
export function termDiscountProblems(d: TermDiscount): string[] {
  return (Object.keys(DEFAULT_TERM_DISCOUNT) as (keyof TermDiscount)[])
    .filter((t) => !(Number.isFinite(d[t]) && d[t] >= 0 && d[t] <= 50 && Math.round(d[t] * 100) === d[t] * 100))
    .map((t) => `${t}-day discount must be 0 to 50 %`);
}
